/* Swallow & Co — interactions for sw-* sections */
(() => {
  const ready = (fn) => (document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn));

  function initDrawer(root = document) {
    root.querySelectorAll('[data-sw-burger]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      const drawer = document.getElementById(btn.getAttribute('aria-controls'));
      if (!drawer) return;
      const close = () => { drawer.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); document.documentElement.style.overflow = ''; };
      btn.addEventListener('click', () => {
        drawer.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); document.documentElement.style.overflow = 'hidden';
      });
      drawer.querySelectorAll('[data-sw-close]').forEach((c) => c.addEventListener('click', close));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    });
  }

  function initSliders(root = document) {
    root.querySelectorAll('[data-sw-slider]').forEach((slider) => {
      if (slider.dataset.bound) return;
      slider.dataset.bound = '1';
      const track = slider.querySelector('[data-sw-track]');
      const prev = slider.querySelector('[data-sw-prev]');
      const next = slider.querySelector('[data-sw-next]');
      if (!track) return;
      const step = () => {
        const slide = track.children[0];
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        return slide.getBoundingClientRect().width + gap;
      };
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();

      let down = false, startX = 0, startLeft = 0, moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
        track.style.scrollSnapType = 'none'; track.style.scrollBehavior = 'auto';
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false; track.style.scrollSnapType = ''; track.style.scrollBehavior = '';
      });
      track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      track.addEventListener('dragstart', (e) => e.preventDefault());

      if (slider.hasAttribute('data-sw-center') && track.children.length > 2) {
        requestAnimationFrame(() => {
          const s = track.children[1];
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = s.offsetLeft - (track.clientWidth - s.clientWidth) / 2;
          track.style.scrollBehavior = '';
        });
      }
    });
  }

  function initScrollColor(root = document) {
    const els = root.querySelectorAll('[data-sw-scroll-dark]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const r = en.boundingClientRect;
        en.target.classList.toggle('is-dark', en.isIntersecting && r.top < window.innerHeight * 0.35);
      });
    }, { threshold: Array.from({ length: 21 }, (_, i) => i / 20) });
    els.forEach((el) => io.observe(el));
  }

  function initReveal(root = document) {
    const els = root.querySelectorAll('.sw-reveal:not(.is-in)');
    if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  }

  function initAccordions(root = document) {
    root.querySelectorAll('[data-sw-accordion]').forEach((group) => {
      if (group.dataset.bound) return;
      group.dataset.bound = '1';
      const items = group.querySelectorAll('details');
      items.forEach((d) => d.addEventListener('toggle', () => {
        if (d.open && group.hasAttribute('data-sw-single')) items.forEach((o) => { if (o !== d) o.open = false; });
      }));
    });
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-sw-cart-count]').forEach((el) => { el.textContent = count; });
  }

  function toast(title) {
    let t = document.querySelector('.sw-toast');
    if (!t) return;
    t.querySelector('.sw-toast__title').textContent = title;
    t.classList.add('is-visible');
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove('is-visible'), 6000);
    const c = t.querySelector('[data-sw-toast-close]');
    if (c && !c.dataset.bound) { c.dataset.bound = '1'; c.addEventListener('click', () => t.classList.remove('is-visible')); }
  }

  function initAddToCart(root = document) {
    root.querySelectorAll('form[data-sw-atc]').forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      const btn = form.querySelector('.sw-atc');
      const select = form.querySelector('[data-sw-variant]');
      if (select && btn) {
        select.addEventListener('change', () => {
          const opt = select.selectedOptions[0];
          btn.disabled = opt.dataset.available !== 'true';
          const price = opt.dataset.price;
          const priceEl = form.closest('[data-sw-pdp]')?.querySelector('[data-sw-price]');
          if (priceEl && price) priceEl.textContent = price;
        });
      }
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!btn) return form.submit();
        const label = btn.innerHTML;
        btn.disabled = true;
        try {
          const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || 'Error');
          const cart = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js').then((r) => r.json());
          setCartCount(cart.item_count);
          btn.innerHTML = btn.dataset.added || 'Added';
          toast(data.product_title || data.title || '');
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'sw-atc' } }));
        } catch (err) {
          btn.innerHTML = err.message;
        } finally {
          setTimeout(() => { btn.innerHTML = label; btn.disabled = false; }, 2200);
        }
      });
    });
  }

  function initAll(root) {
    initDrawer(root); initSliders(root); initScrollColor(root); initReveal(root); initAccordions(root); initAddToCart(root);
  }

  ready(() => initAll(document));
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();